import React, { useEffect, useRef, useState } from 'react'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, Chip, CircularProgress, LinearProgress, MenuItem, Select, Typography } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { userApiSlice } from '../../../features/user/userSlice'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import CustomInput from '../../../components/muiComponents/customInput'
import StudentsTable from './components/StudentsTable'
import WithdrawnTable from './components/WithdrawnTable'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import { ui } from '../../../utils/uiTokens'
import { capitalize } from '../../../utils/functions'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { useInfiniteScroll } from '../../../app/hooks/useInfiniteScroll'
import { CuratorStudentState } from '../../../features/types'
import { useGroupLabel, useHeadScope } from '../../head/headScope'
import DirectorSubjectFilter from '../../director/DirectorSubjectFilter'

// У басшы курс и группа общие для всех страниц кабинета (ключи рейтинга)
const storedId = (key: string) => {
    try {
        return Number(JSON.parse(localStorage.getItem(key) || 'null')) || undefined
    } catch {
        return undefined
    }
}

const styles = {
    boxContainer: {
        margin: '10px',
        borderRadius: '16px',
    },
    filterBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
    },
    search: {
        width: '100%',
        maxWidth: '440px',
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: 2.5,
    },
    filterGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
    },
    groupSelect: {
        minWidth: 190,
        borderRadius: ui.radius,
        fontSize: '13.5px',
        fontWeight: 600,
        color: ui.ink,
        backgroundColor: ui.card,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    },
}

const StudentListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage, locale } = useIntl()
    const { readOnly } = useHeadScope()
    const groupLabel = useGroupLabel()
    const [filter, setFilter] = useState('')
    const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(
        Number(searchParams.get('courseId')) || (readOnly ? storedId('curator.courseId') : undefined),
    )
    const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(
        Number(searchParams.get('groupId')) || (readOnly ? storedId('curator.groupId') : undefined),
    )
    const [monthNumber, setMonthNumber] = usePersistedState<number | undefined>(
        'curator.monthNumber',
        Number(searchParams.get('monthNumber')) || undefined,
    )
    const [weekNumber, setWeekNumber] = usePersistedState<number | undefined>(
        'curator.weekNumber',
        Number(searchParams.get('weekNumber')) || undefined,
    )
    const [search, setSearch] = useState('')
    const [withdrawnTab, setWithdrawnTab] = useState(searchParams.get('tab') === 'withdrawn')
    // Сортировка: 0 нет, 1 видео, 2 тест, 3 соңғы кіру (1 - давно не заходившие сверху); направление 0/1 убыв/2 возр
    const [sortColumn, setSortColumn] = useState(0)
    const [sortDir, setSortDir] = useState(0)
    // Фильтр диапазона процента: 0 все, 1 = 0-50%, 2 = больше 50%
    const [videoFilter, setVideoFilter] = useState(0)
    const [testFilter, setTestFilter] = useState(0)
    const [rows, setRows] = useState<CuratorStudentState[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [loadedPages, setLoadedPages] = useState(0)
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)
    // Наблюдатель может сработать до того, как новая порция легла в state,
    // и снова попросить ту же страницу: она бы добавилась в список второй раз
    const loadedPagesRef = useRef(0)

    const [fetchStudents, { isFetching }] =
        curatorStudentsApiSlice.useLazyFetchAllStudentsQuery()
    const [fetchCourses, { data: courseResponse = null }] =
        curatorStudentsApiSlice.useLazyFetchCuratorCoursesQuery()
    const { data: groups = [] } = curatorStudentsApiSlice.useFetchCuratorGroupsQuery(
        selectedCourseId,
        { skip: !selectedCourseId },
    )
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()

    const hasMore = rows.length < totalCount

    const loadPage = async (nextPage: number) => {
        if (!selectedCourseId) return
        if (nextPage > 1 && (isLoadingRef.current || nextPage <= loadedPagesRef.current)) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true

        try {
            const response = await fetchStudents({
                filter: search,
                page: nextPage - 1,
                size: DEFAULT_PAGE_SIZE,
                courseId: selectedCourseId,
                groupId: selectedGroupId,
                monthNumber,
                weekNumber,
                sortColumn,
                sortDir,
                videoFilter,
                testFilter,
            }).unwrap()

            if (requestId !== requestIdRef.current) return

            setRows((prev) => (nextPage === 1 ? response.data : [...prev, ...response.data]))
            // Короткая страница означает конец списка: расхождение с totalCount
            // иначе гоняло бы наблюдатель по пустым страницам без остановки
            setTotalCount(response.data.length < DEFAULT_PAGE_SIZE
                ? (nextPage - 1) * DEFAULT_PAGE_SIZE + response.data.length
                : response.totalCount)
            setLoadedPages(nextPage)
            loadedPagesRef.current = nextPage
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) setTotalCount((nextPage - 1) * DEFAULT_PAGE_SIZE)
        } finally {
            if (requestId === requestIdRef.current) isLoadingRef.current = false
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        hasMore && !isFetching && loadedPages > 0,
    )

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            loadPage(1)
        } catch (error) { }
    }

    const handleCourseClick = (courseId: number) => {
        setSelectedCourseId(courseId)
        setSelectedGroupId(undefined)
    }

    // Проценты считаются по темам выбранного периода
    const handlePeriodChange = (month?: number, week?: number) => {
        setMonthNumber(month)
        setWeekNumber(week)
    }

    // Клик по заголовку: первый раз по убыванию, второй по возрастанию
    const handleSort = (column: number) => {
        if (sortColumn !== column) {
            setSortColumn(column)
            setSortDir(1)
        } else {
            setSortDir((prev) => (prev === 1 ? 2 : 1))
        }
    }

    useEffect(() => {
        fetchCourses()
    }, [])

    // Курс и группа живут в URL, иначе после перезагрузки список молча
    // возвращается к первому курсу
    useEffect(() => {
        const params: Record<string, string> = {}
        if (selectedCourseId) params.courseId = selectedCourseId.toString()
        if (selectedGroupId) params.groupId = selectedGroupId.toString()
        if (monthNumber) params.monthNumber = monthNumber.toString()
        if (weekNumber) params.weekNumber = weekNumber.toString()
        if (withdrawnTab) params.tab = 'withdrawn'
        setSearchParams(params, { replace: true })
        if (readOnly) {
            try {
                localStorage.setItem('curator.courseId', JSON.stringify(selectedCourseId ?? null))
                localStorage.setItem('curator.groupId', JSON.stringify(selectedGroupId ?? null))
            } catch { }
        }

        setRows([])
        setTotalCount(0)
        setLoadedPages(0)
        loadedPagesRef.current = 0
        if (!withdrawnTab) loadPage(1)
    }, [selectedCourseId, selectedGroupId, monthNumber, weekNumber, search, sortColumn, sortDir, videoFilter, testFilter, withdrawnTab])

    useEffect(() => {
        if (courseResponse?.data?.length > 0 && !selectedCourseId) {
            setSelectedCourseId(courseResponse.data[0].id)
        }
    }, [courseResponse])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'students' })}</H3>
                    </Title>
                    <Tabs>
                        <Tab active={!withdrawnTab} type="button" onClick={() => setWithdrawnTab(false)}>
                            {formatMessage({ id: 'activeStudentsTab' })}
                        </Tab>
                        <Tab active={withdrawnTab} type="button" onClick={() => setWithdrawnTab(true)}>
                            {formatMessage({ id: 'withdrawnTab' })}
                        </Tab>
                    </Tabs>
                </Header>

                {!withdrawnTab && <Box sx={styles.filterBox}>
                    <Box sx={styles.searchBox}>
                        <CustomInput
                            sx={styles.search}
                            value={filter}
                            margin="none"
                            type="text"
                            placeholder={formatMessage({
                                id: 'searchByStudentName',
                            })}
                            size="small"
                            onChange={(e) => setFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearch(filter)
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={() => setSearch(filter)}
                        >
                            {formatMessage({ id: 'search' })}
                        </Button>
                    </Box>
                </Box>}
                <ChipGroup>
                        {courseResponse?.data?.map((course) => (
                            <Chip
                                label={course[`title${capitalize(locale)}`]}
                                key={course.id}
                                onClick={() => handleCourseClick(course.id)}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: selectedCourseId === course.id ? 600 : 400,
                                    background: selectedCourseId === course.id ? ui.pinkSoft : 'transparent',
                                    border: `1px solid ${selectedCourseId === course.id ? 'transparent' : ui.border}`,
                                    color: selectedCourseId === course.id ? ui.pink : ui.sub,
                                    '&:hover': { background: ui.pinkSoft, color: ui.pink },
                                }}
                            />
                        ))}
                </ChipGroup>

                <Box sx={styles.filterRow}>
                    <Box sx={styles.filterGroup}>
                        <DirectorSubjectFilter byHead />
                        <Select
                            size="small"
                            displayEmpty
                            value={selectedGroupId ?? ''}
                            onChange={(e) => setSelectedGroupId(Number(e.target.value) || undefined)}
                            sx={styles.groupSelect}
                        >
                            <MenuItem value="">{formatMessage({ id: 'allGroups' })}</MenuItem>
                            {groups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {groupLabel(group)}
                                </MenuItem>
                            ))}
                        </Select>

                        {!withdrawnTab && <>
                        <MonthWeekFilter
                            courseId={selectedCourseId ?? null}
                            courseSubjectId={(groups.find((g) => g.id === selectedGroupId) ?? (readOnly ? undefined : groups[0]))?.courseSubjectId}
                            groupId={selectedGroupId}
                            monthNumber={monthNumber}
                            weekNumber={weekNumber}
                            onChange={handlePeriodChange}
                        />

                        <Select
                            size="small"
                            value={videoFilter}
                            onChange={(e) => setVideoFilter(Number(e.target.value))}
                            sx={styles.groupSelect}
                        >
                            <MenuItem value={0}>{capitalize(formatMessage({ id: 'videoLesson' }))}: {formatMessage({ id: 'all' })}</MenuItem>
                            <MenuItem value={1}>{capitalize(formatMessage({ id: 'videoLesson' }))}: {formatMessage({ id: 'percent0to50' })}</MenuItem>
                            <MenuItem value={2}>{capitalize(formatMessage({ id: 'videoLesson' }))}: {formatMessage({ id: 'percentOver50' })}</MenuItem>
                        </Select>

                        <Select
                            size="small"
                            value={testFilter}
                            onChange={(e) => setTestFilter(Number(e.target.value))}
                            sx={styles.groupSelect}
                        >
                            <MenuItem value={0}>{formatMessage({ id: 'test' })}: {formatMessage({ id: 'all' })}</MenuItem>
                            <MenuItem value={1}>{formatMessage({ id: 'test' })}: {formatMessage({ id: 'percent0to50' })}</MenuItem>
                            <MenuItem value={2}>{formatMessage({ id: 'test' })}: {formatMessage({ id: 'percentOver50' })}</MenuItem>
                        </Select>
                        </>}
                    </Box>

                    {!withdrawnTab && <Typography variant="body1" sx={{ color: ui.sub }}>
                        {formatMessage({ id: 'all' })} {' : '}
                        <span style={{ fontWeight: 600, color: ui.pink }}>{totalCount} {formatMessage({ id: 'student' })}</span>
                    </Typography>}
                </Box>
                {withdrawnTab ? (
                    <WithdrawnTable courseId={selectedCourseId} groupId={selectedGroupId} />
                ) : (isFetching && loadedPages === 0) || isReseting ? (
                    <LinearProgress />
                ) : (
                    <>
                        <StudentsTable
                            studentsData={rows}
                            isFetching={isFetching}
                            onResetPassword={handleResetPassword}
                            sortColumn={sortColumn}
                            sortDir={sortDir}
                            onSort={handleSort}
                            courseSubjectId={groups.find((group) => group.id === selectedGroupId)?.courseSubjectId}
                        />

                        <Sentinel ref={sentinelRef}>
                            {isFetching && <CircularProgress size={22} sx={{ color: ui.pink }} />}
                        </Sentinel>
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default StudentListPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    color: ${ui.ink};
`

const Content = styled.div`
    background-color: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
`

const Sentinel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
`

const Tabs = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 20px;
`

const Tab = styled.button<{ active: boolean }>`
    cursor: pointer;
    font: inherit;
    font-size: 13.5px;
    line-height: 1.2;
    padding: 8px 16px;
    border-radius: 12px;
    white-space: nowrap;
    background: ${({ active }) => (active ? ui.pinkSoft : 'transparent')};
    border: 1px solid ${({ active }) => (active ? 'transparent' : ui.border)};
    color: ${({ active }) => (active ? ui.pink : ui.sub)};
    font-weight: ${({ active }) => (active ? 600 : 400)};

    &:hover {
        border-color: ${ui.pink};
        color: ${ui.pink};
    }
`

const ChipGroup = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin: 20px 0 16px;
`